import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';

import { tokens } from '../../../theme-next';
import type { EntityKindOverrides, NatureChoisie, NatureEntite } from '../validation/personneMorale';

/**
 * Bascule « particulier / société » pour le vendeur et l'acquéreur.
 *
 * La nature d'une partie est normalement DÉDUITE des pièces (`natureVendeur` /
 * `natureAcheteur`). Cette déduction lit des documents : elle ne peut pas savoir
 * qu'un Kbis a été déposé par erreur, ni qu'un vendeur dont la cession porte un
 * SIRET cède en réalité son véhicule personnel. Jusqu'ici l'agence n'avait aucun
 * moyen de la contredire — la modale god a son sélecteur depuis toujours, pas
 * l'écran agence (retour François 2026-09-18).
 *
 * Trois états, pas deux : tant que l'agence n'a rien choisi, c'est la règle qui
 * décide et on le dit. Cliquer sur la valeur déjà active rend la main à la règle
 * plutôt que de figer le dossier sur une valeur qu'on n'a pas vraiment voulue.
 */
interface EntityKindSwitchesProps {
  vendeurNature: NatureEntite;
  acheteurNature: NatureEntite;
  overrides: EntityKindOverrides;
  onChange: (partie: 'vendeur' | 'acheteur', nature: NatureChoisie | null) => void;
}

const LIBELLES: Record<NatureChoisie, string> = {
  particulier: 'Particulier',
  pm: 'Société',
};

const Ligne = ({
  partie,
  label,
  nature,
  override,
  onChange,
}: {
  partie: 'vendeur' | 'acheteur';
  label: string;
  nature: NatureEntite;
  override?: NatureChoisie;
  onChange: EntityKindSwitchesProps['onChange'];
}) => (
  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
    <Typography variant="body2" sx={{ minWidth: 76, color: tokens.color.textMuted }}>
      {label}
    </Typography>

    {(['particulier', 'pm'] as const).map((valeur) => {
      const actif = nature === valeur;
      return (
        <Chip
          key={valeur}
          label={LIBELLES[valeur]}
          size="small"
          // Cliquer sur l'état déjà actif relâche le choix : l'agence peut
          // revenir à la déduction sans recharger le dossier.
          onClick={() => onChange(partie, override === valeur ? null : valeur)}
          color={actif ? 'primary' : 'default'}
          variant={actif ? 'filled' : 'outlined'}
          sx={{ cursor: 'pointer' }}
        />
      );
    })}

    {override ? (
      <Tooltip title="Vous avez imposé ce choix. Cliquez à nouveau dessus pour revenir à la détection automatique.">
        <Typography variant="caption" sx={{ color: tokens.color.textMuted }}>
          choix manuel
        </Typography>
      </Tooltip>
    ) : nature === 'indecis' ? (
      <Typography variant="caption" sx={{ color: tokens.color.textMuted }}>
        les pièces se contredisent, les deux blocs restent affichés
      </Typography>
    ) : (
      <Typography variant="caption" sx={{ color: tokens.color.textMuted }}>
        d'après les pièces
      </Typography>
    )}
  </Stack>
);

export const EntityKindSwitches = ({
  vendeurNature,
  acheteurNature,
  overrides,
  onChange,
}: EntityKindSwitchesProps) => (
  <Box sx={{ mb: 2 }}>
    <Stack spacing={1}>
      <Ligne
        partie="vendeur"
        label="Vendeur"
        nature={vendeurNature}
        override={overrides.vendeur}
        onChange={onChange}
      />
      <Ligne
        partie="acheteur"
        label="Acheteur"
        nature={acheteurNature}
        override={overrides.acheteur}
        onChange={onChange}
      />
    </Stack>
  </Box>
);
